import type {
  Allocation,
  Company,
  CompanyInput,
  Tenant,
  TenantHealth,
  TenantInput,
  ClockInput,
  CloseOrderInput,
  ComponentToIssue,
  FinishPhaseInput,
  MonitorRow,
  Oee,
  PausePhaseInput,
  Phase,
  ProductionOrder,
  StartPhaseInput,
  StopReason,
  TerminalState,
} from '@ecinsa/shared';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/** Fallo de red: la acción se puede encolar y reintentar más tarde. */
export class OfflineError extends Error {
  constructor() {
    super('Sin conexión con el servidor');
  }
}

const TOKEN_KEY = 'ecinsa.token';
const DEVICE_KEY = 'ecinsa.device';

export function deviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = `term-${crypto.randomUUID().slice(0, 8)}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  let response: Response;
  try {
    response = await fetch(path, {
      ...options,
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });
  } catch {
    throw new OfflineError();
  }

  if (response.status === 204) return undefined as T;

  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (body as { error?: { code: string; message: string; details?: unknown } } | null)?.error;
    throw new ApiError(
      error?.code ?? 'INTERNAL',
      error?.message ?? `Error ${response.status}`,
      response.status,
      error?.details,
    );
  }
  return body as T;
}

export interface Catalog {
  phases: Phase[];
  stopReasons: StopReason[];
  orders: ProductionOrder[];
  parameters: Record<string, string>;
  syncedAt: string | null;
  serverTime: string;
}

export const api = {
  login: (pin: string) =>
    request<{ token: string; state: TerminalState }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ pin, deviceId: deviceId() }),
    }),

  me: () => request<{ state: TerminalState | null }>('/api/me'),

  /** Identidades de demostración. 404 si el cliente no lo tiene activado. */
  demoIdentities: () =>
    request<{
      operario: { employeeId: number; fullName: string; puedeImputar: boolean } | null;
      jefe: { employeeId: number; fullName: string; puedeImputar: boolean } | null;
    }>('/api/auth/demo-identities'),

  demoLogin: (tipo: 'operario' | 'usuario', id: string) =>
    request<{
      tipo: 'operario' | 'usuario';
      token: string;
      state?: TerminalState;
      user?: UsuarioTerminal;
      tenant?: { slug: string; name: string };
    }>('/api/auth/demo-login', { method: 'POST', body: JSON.stringify({ tipo, id, deviceId: deviceId() }) }),

  catalog: () => request<Catalog>('/api/catalog'),

  order: (docEntry: number) =>
    request<{ order: ProductionOrder; phases: { code: string; enabled: boolean; reason: string | null }[] }>(
      `/api/orders/${docEntry}`,
    ),

  components: (docEntry: number, phase: string, quantity: number) =>
    request<{ components: ComponentToIssue[] }>(
      `/api/orders/${docEntry}/components?phase=${encodeURIComponent(phase)}&quantity=${quantity}`,
    ),

  availability: (itemCode: string, warehouse: string, managedBy: 'batch' | 'serial') =>
    request<{ availability: { code: string; binAbsEntry: number | null; binCode: string | null; available: number }[] }>(
      `/api/availability?itemCode=${encodeURIComponent(itemCode)}&warehouse=${encodeURIComponent(warehouse)}&managedBy=${managedBy}`,
    ),

  /** Ubicaciones del almacén donde entra lo fabricado. */
  bins: (warehouse: string) =>
    request<{ bins: { absEntry: number; code: string; description: string | null }[] }>(
      `/api/bins?warehouse=${encodeURIComponent(warehouse)}`,
    ),

  clock: (input: ClockInput) =>
    request<{ state: TerminalState }>('/api/presence/clock', { method: 'POST', body: JSON.stringify(input) }),

  startPhase: (input: StartPhaseInput) =>
    request<{ state: TerminalState }>('/api/production/start', { method: 'POST', body: JSON.stringify(input) }),

  pausePhase: (input: PausePhaseInput) =>
    request<{ state: TerminalState }>('/api/production/pause', { method: 'POST', body: JSON.stringify(input) }),

  finishPhase: (input: FinishPhaseInput) =>
    request<{ state: TerminalState; laborSeconds: number }>('/api/production/finish-phase', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  closeOrder: (input: CloseOrderInput) =>
    request<{ state: TerminalState }>('/api/production/close-order', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  sync: (actions: { kind: string; payload: unknown }[]) =>
    request<{ results: { index: number; ok: boolean; code?: string; message?: string }[]; state: TerminalState }>(
      '/api/sync',
      { method: 'POST', body: JSON.stringify({ actions }) },
    ),

  board: () =>
    request<{
      rows: MonitorRow[];
      outbox: Record<string, number>;
      syncedAt: string | null;
      sapError: string | null;
      serverTime: string;
    }>('/api/monitor/board'),

  /** El pulso del taller: horas, paradas, carga por fase y quién trabaja. */
  pulso: () => request<PulsoTaller>('/api/monitor/pulso'),

  /** Todo lo que se sabe de una OF: fases, imputaciones, recibos y documentos. */
  ficha: (docEntry: number) => request<FichaOrden>(`/api/monitor/orden/${docEntry}`),

  /** Buscador general: el mismo texto contra OFs, gente, fases, causas y documentos. */
  buscar: (q: string) => request<{ resultados: Busqueda }>(`/api/monitor/buscar?q=${encodeURIComponent(q)}`),

  oee: (hours = 24) => request<{ oee: Oee }>(`/api/monitor/oee?hours=${hours}`),

  context: () =>
    request<{
      mode: 'admin' | 'tenant' | 'unknown';
      tenantDomain: string;
      tenant: { slug: string; name: string; active: boolean; configured: boolean } | null;
    }>('/api/context'),

  parameters: () =>
    request<{ definitions: { key: string; label: string; help: string; type: string; group: string; legacy?: string }[]; values: Record<string, string> }>(
      '/api/settings/parameters',
    ),

  saveParameters: (values: Record<string, string>) =>
    request<{ values: Record<string, string> }>('/api/settings/parameters', {
      method: 'PUT',
      body: JSON.stringify({ values }),
    }),

  outbox: () =>
    request<{ items: { id: string; kind: string; status: string; summary: string; lastError: string | null; attempts: number; createdAt: string; sapDocNum: string | null }[]; stats: Record<string, number> }>(
      '/api/settings/outbox',
    ),

  retryOutbox: (id: string) => request<{ ok: true }>(`/api/settings/outbox/${id}/retry`, { method: 'POST' }),

  forceSync: () => request<{ syncedAt: string | null; error: string | null }>('/api/settings/sync', { method: 'POST' }),
};

export interface UsuarioTerminal {
  id: string;
  username: string;
  fullName: string;
  role: 'director' | 'jefe' | 'operario';
  employeeId: number | null;
  active: boolean;
  lastLoginAt: string | null;
  /** Cuenta de ECINSA: entra en todos los clientes, no solo en este. */
  global?: boolean;
}

/** Lo que encuentra el buscador, agrupado por lo que es cada cosa. */
export interface Busqueda {
  consulta: string;
  ordenes: {
    docEntry: number; docNum: string; itemCode: string; itemDescription: string; status: string;
    dueDate: string | null; plannedQuantity: number; completedQuantity: number;
    customerName: string | null; project: string | null;
  }[];
  personas: {
    employeeId: number; nombre: string; puesto: string | null; recursos: string[];
    trabajandoAhora: boolean; segundosHoy: number;
  }[];
  fases: { code: string; name: string; segundos: number; ofs: number }[];
  causas: { code: string; name: string; tipo: string; veces: number; segundos: number }[];
  documentos: {
    tipo: string; estado: string; resumen: string; sapDocNum: string | null;
    error: string | null; docEntry: number | null; cuando: string;
  }[];
}

/** Todo lo que se sabe de una orden de fabricación. */
export interface FichaOrden {
  orden: ProductionOrder;
  fases: {
    code: string;
    previstoSegundos: number;
    segundos: number;
    parado: number;
    operarios: string[];
    enMarcha: boolean;
  }[];
  imputaciones: {
    id: string;
    employeeId: number;
    nombre: string;
    fase: string;
    recurso: string | null;
    desde: string;
    hasta: string | null;
    segundos: number;
    estado: string;
    causa: string | null;
    emitido: boolean;
    origen: string;
  }[];
  fasesFinalizadas: {
    fase: string;
    por: string | null;
    cantidad: number;
    rechazo: number;
    causaRechazo: string | null;
    segundos: number;
    cuando: string;
    enviadoASap: boolean;
  }[];
  recibos: { cantidad: number; rechazo: number; cuando: string; por: string | null; sapDocNum: string | null; origen: string }[];
  documentos: { tipo: string; estado: string; resumen: string; sapDocNum: string | null; error: string | null; intentos: number; cuando: string }[];
}

/** Lo que la pantalla del taller enseña además del tablero de OFs. */
export interface PulsoTaller {
  horas: { hora: string; run: number; stop: number; piezas: number }[];
  paradas: { code: string; name: string; seconds: number; veces: number }[];
  fases: { fase: string; seconds: number; operarios: number }[];
  trabajando: { employeeId: number; nombre: string; docNum: string | null; fase: string | null; desde: string | null }[];
  oee: Oee;
  serverTime: string;
}

/** Panel de dirección: sesión con usuario y contraseña. */
export const direccionApi = {
  login: (username: string, password: string) =>
    request<{ token: string; user: UsuarioTerminal; tenant: { slug: string; name: string } }>(
      '/api/auth/user-login',
      { method: 'POST', body: JSON.stringify({ username, password }) },
    ),

  me: () => request<{ user: UsuarioTerminal; tenant: { slug: string; name: string } }>('/api/direccion/me'),

  overview: (dias = 14) => request<Overview>(`/api/direccion/overview?dias=${dias}`),

  planDeReserva: (docEntry: number) => request<PlanDeReserva>(`/api/direccion/reservas/${docEntry}`),

  reservar: (docEntry: number, lineas: { lineNum: number; quantity: number; allocations: Allocation[] }[]) =>
    request<{ outboxId: string; ubicacion: string; lineas: number }>(`/api/direccion/reservas/${docEntry}`, {
      method: 'POST',
      body: JSON.stringify({ lineas }),
    }),

  tiempos: (dias = 180) => request<AnalisisTiempos>(`/api/direccion/tiempos?dias=${dias}`),

  completarTiempos: () =>
    request<{ faltaban: number; traidas: number; noEncontradas: number }>('/api/direccion/tiempos/completar', {
      method: 'POST',
    }),

  outbox: () =>
    request<{
      items: { id: string; kind: string; status: string; summary: string; lastError: string | null; attempts: number; createdAt: string; sapDocNum: string | null }[];
      stats: Record<string, number>;
    }>('/api/direccion/outbox'),

  retry: (id: string) => request<{ ok: true }>(`/api/direccion/outbox/${id}/retry`, { method: 'POST' }),

  operarios: () =>
    request<{ operarios: OperarioTerminal[]; campoPin: string; rolJefe: string }>('/api/direccion/operarios'),

  asignarPin: (employeeId: number, pin?: string) =>
    request<{ pin: string; sobrescrito: string | null }>(`/api/direccion/operarios/${employeeId}/pin`, {
      method: 'POST',
      body: JSON.stringify(pin ? { pin } : {}),
    }),

  quitarPin: (employeeId: number) =>
    request<{ ok: true }>(`/api/direccion/operarios/${employeeId}/pin`, { method: 'DELETE' }),

  // ── Datos maestros que antes solo se tocaban en SAP ──
  recursos: () =>
    request<{
      recursos: { code: string; name: string; employees: number[] }[];
      roles: { id: number; name: string }[];
      rolJefe: string;
    }>('/api/maestros/recursos'),

  asignarRecurso: (code: string, employeeId: number, asignado: boolean, activar = false) =>
    request<{ ok: true; recursos: { code: string; name: string; employees: number[] }[] }>(
      `/api/maestros/recursos/${encodeURIComponent(code)}/empleados/${employeeId}`,
      { method: 'POST', body: JSON.stringify({ asignado, activar }) },
    ),

  rolJefe: (employeeId: number, esJefe: boolean) =>
    request<{ ok: true }>(`/api/maestros/empleados/${employeeId}/rol-jefe`, {
      method: 'POST',
      body: JSON.stringify({ esJefe }),
    }),

  catalogosEmpleado: () =>
    request<{
      departments: { code: number; name: string }[];
      branches: { code: number; name: string }[];
      recursos: { code: string; name: string }[];
      rolJefe: string;
    }>('/api/maestros/empleados/catalogos'),

  crearEmpleado: (input: {
    firstName: string;
    lastName: string;
    jobTitle?: string | null;
    department?: number | null;
    branch?: number | null;
    pin?: string;
    resourceCode?: string | null;
    esJefe?: boolean;
  }) =>
    request<{ employeeId: number; pin: string; hechos: string[]; avisos: string[] }>('/api/maestros/empleados', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  sincronizar: () =>
    request<{
      antes: { empleados: number; conPin: number; ordenes: number; fases: number; causas: number };
      despues: { empleados: number; conPin: number; ordenes: number; fases: number; causas: number };
      error: string | null;
      syncedAt: string | null;
    }>('/api/maestros/sincronizar', { method: 'POST', body: '{}' }),

  historico: () => request<{ resumen: ResumenHistorico }>('/api/maestros/historico'),
  importarHistorico: () =>
    request<{ importados: number; repetidos: number; descartados: number; recibos: number }>('/api/maestros/historico/importar', {
      method: 'POST',
      body: '{}',
    }),

  fases: () => request<{ fases: Record<string, string>[]; tabla: string }>('/api/maestros/fases'),
  crearFase: (code: string, name: string) =>
    request<{ ok: true }>('/api/maestros/fases', { method: 'POST', body: JSON.stringify({ code, name }) }),
  editarFase: (code: string, name: string) =>
    request<{ ok: true }>(`/api/maestros/fases/${encodeURIComponent(code)}`, { method: 'PUT', body: JSON.stringify({ name }) }),
  borrarFase: (code: string) =>
    request<{ ok: true }>(`/api/maestros/fases/${encodeURIComponent(code)}`, { method: 'DELETE' }),

  causas: () =>
    request<{ causas: Record<string, string>[]; tabla: string; campoTipo: string; campoActivo: string }>(
      '/api/maestros/causas',
    ),
  guardarCausa: (input: { code: string; name: string; tipo: 'F' | 'P'; activa: boolean }, existente: boolean) =>
    request<{ ok: true }>(
      existente ? `/api/maestros/causas/${encodeURIComponent(input.code)}` : '/api/maestros/causas',
      { method: existente ? 'PUT' : 'POST', body: JSON.stringify(input) },
    ),
  borrarCausa: (code: string) =>
    request<{ ok: true }>(`/api/maestros/causas/${encodeURIComponent(code)}`, { method: 'DELETE' }),

  users: () =>
    request<{ users: UsuarioTerminal[]; puedeCrear: ('director' | 'jefe' | 'operario')[] }>('/api/direccion/users'),

  createUser: (input: { username: string; fullName: string; role: string; password?: string; employeeId?: number | null }) =>
    request<{ user: UsuarioTerminal; password: string }>('/api/direccion/users', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  reactivar: (id: string) =>
    request<{ ok: true; username: string }>(`/api/direccion/users/${id}/reactivate`, { method: 'POST' }),

  darDeBaja: (id: string) =>
    request<{ ok: true; username: string }>(`/api/direccion/users/${id}`, { method: 'DELETE' }),

  resetPassword: (id: string, password?: string) =>
    request<{ password: string }>(`/api/direccion/users/${id}/password`, {
      method: 'POST',
      body: JSON.stringify(password ? { password } : {}),
    }),
};

export interface OperarioTerminal {
  employeeId: number;
  fullName: string;
  jobTitle: string | null;
  hasPin: boolean;
  supervisor: boolean;
  resourceCodes: string[];
  faltan: string[];
}

export interface ResumenHistorico {
  registros: number;
  recibos: number;
  recibosYaImportados: number;
  operarios: number;
  ordenes: number;
  fases: string[];
  desde: string | null;
  hasta: string | null;
  conCausa: number;
  yaImportados: number;
  descartados: { motivo: string; registros: number }[];
}


/** Una comparación entre lo que la ruta de SAP prevé y lo que costó de verdad. */
export interface Desviacion {
  clave: string;
  etiqueta: string;
  sub: string | null;
  ofs: number;
  unidades: number;
  previsto: number;
  real: number;
  parado: number;
}

export interface DesviacionOf extends Desviacion {
  docEntry: number;
  docNum: string;
  fase: string;
  ultima: string | null;
}

export interface AnalisisTiempos {
  ventanaDias: number;
  resumen: {
    tramos: number;
    comparados: number;
    previsto: number;
    real: number;
    parado: number;
    ofs: number;
    unidades: number;
  };
  excluidos: { motivo: string; pares: number; segundos: number }[];
  ofsSinFicha: number;
  porArticulo: Desviacion[];
  porFase: Desviacion[];
  porRecurso: Desviacion[];
  ofs: DesviacionOf[];
  tramosSinRecurso: number;
}

/** Componente de una OF con lo que hay que apartar para fabricarla. */
export interface ComponenteReservable {
  lineNum: number;
  itemCode: string;
  description: string;
  warehouse: string;
  uom: string | null;
  managedBy: 'none' | 'batch' | 'serial';
  necesario: number;
  reservado: number;
  pendiente: number;
  disponible: { code: string; binAbsEntry: number | null; binCode: string | null; available: number }[];
}

export interface PlanDeReserva {
  docEntry: number;
  docNum: string;
  itemCode: string;
  itemDescription: string;
  warehouse: string;
  ubicacion: string;
  ubicacionExiste: boolean;
  activa: boolean;
  componentes: ComponenteReservable[];
}

export interface Overview {
  oee: { oee: number; availability: number; performance: number; quality: number; runSeconds: number; stopSeconds: number; producedQty: number; scrapQty: number; windowHours: number };
  ahora: { dentro: number; imputando: number; ofsEnMarcha: number; incidenciasAbiertas: number };
  turno: { producido: number; rechazado: number; segundosProduciendo: number; segundosParado: number; fasesFinalizadas: number };
  periodo: {
    producido: number; rechazado: number; segundosProduciendo: number; segundosParado: number;
    fasesFinalizadas: number; ordenesConProduccion: number; hayPresencia: boolean;
  };
  produccionPorDia: { date: string; etiqueta: string; good: number; scrap: number; seconds: number }[];
  paradas: { code: string; name: string; seconds: number; count: number }[];
  operarios: { employeeId: number; name: string; runSeconds: number; stopSeconds: number }[];
  cola: { pending: number; failed: number; done: number };
  ventanaDias: number;
  agrupacion: 'dia' | 'semana' | 'mes';
  segundosEnVentana: number;
  tramosHistoricos: number;
  ordenes: { rows: MonitorRow[]; outbox: Record<string, number> };
  ajustes: { reservas: boolean };
  sap: { conectado: boolean; error: string | null; syncedAt: string | null };
  tenant: { slug: string; name: string; companyDb: string };
  serverTime: string;
}

/** Panel de administración: alta y mantenimiento de clientes. */
export const adminApi = {
  login: (password: string, username = '') =>
    request<{ token: string; user: { username: string; fullName: string } | null }>('/api/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  tenantUsers: (slug: string) => request<{ users: UsuarioTerminal[] }>(`/api/admin/tenants/${slug}/users`),

  createTenantUser: (slug: string, input: { username: string; fullName: string; role: string; password?: string }) =>
    request<{ user: UsuarioTerminal; password: string }>(`/api/admin/tenants/${slug}/users`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  tenantOperarios: (slug: string) =>
    request<{
      operarios: OperarioTerminal[];
      departments: { code: number; name: string }[];
      branches: { code: number; name: string }[];
      recursos: { code: string; name: string }[];
      campoPin: string;
      rolJefe: string;
    }>(`/api/admin/tenants/${slug}/operarios`),

  crearTenantOperario: (
    slug: string,
    input: {
      firstName: string; lastName: string; jobTitle?: string | null;
      department?: number | null; branch?: number | null; pin?: string;
      resourceCode?: string | null; esJefe?: boolean;
    },
  ) =>
    request<{ employeeId: number; pin: string; hechos: string[]; avisos: string[] }>(
      `/api/admin/tenants/${slug}/operarios`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  // ── Cuentas de ECINSA: entran en el panel y en todos los clientes ──
  cuentasEcinsa: () =>
    request<{ users: { id: string; username: string; fullName: string; lastLoginAt: string | null }[] }>(
      '/api/admin/users',
    ),

  crearCuentaEcinsa: (username: string, fullName: string, password?: string) =>
    request<{ username: string; password: string }>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify({ username, fullName, ...(password ? { password } : {}) }),
    }),

  passwordCuentaEcinsa: (id: string, password?: string) =>
    request<{ password: string; elegida: boolean }>(`/api/admin/users/${id}/password`, {
      method: 'POST',
      body: JSON.stringify(password ? { password } : {}),
    }),

  bajaCuentaEcinsa: (id: string) => request<{ ok: true }>(`/api/admin/users/${id}`, { method: 'DELETE' }),

  pinTenantOperario: (slug: string, employeeId: number, pin?: string) =>
    request<{ pin: string; sobrescrito: string | null }>(
      `/api/admin/tenants/${slug}/operarios/${employeeId}/pin`,
      { method: 'POST', body: JSON.stringify(pin ? { pin } : {}) },
    ),

  resetTenantUserPassword: (slug: string, id: string, password?: string) =>
    request<{ password: string; elegida: boolean }>(`/api/admin/tenants/${slug}/users/${id}/password`, {
      method: 'POST',
      body: JSON.stringify(password ? { password } : {}),
    }),

  tenants: () =>
    request<{
      tenants: Tenant[];
      health: TenantHealth[];
      hubClients: string[];
      /** Los mismos túneles, con su IP en la VPN cuando el hub la dice. */
      hubTunnels?: { name: string; ip: string | null }[];
      tenantDomain: string;
      live: string[];
    }>('/api/admin/tenants'),

  createTenant: (input: TenantInput) =>
    request<{ tenant: Tenant; health: TenantHealth }>('/api/admin/tenants', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateTenant: (slug: string, input: TenantInput) =>
    request<{ tenant: Tenant; health: TenantHealth }>(`/api/admin/tenants/${slug}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  saveCompany: (slug: string, companyId: string | null, input: CompanyInput) =>
    request<{ company: Company; health: TenantHealth }>(
      `/api/admin/tenants/${slug}/companies${companyId ? `/${companyId}` : ''}`,
      { method: 'PUT', body: JSON.stringify(input) },
    ),

  deleteCompany: (slug: string, companyId: string) =>
    request<{ health: TenantHealth }>(`/api/admin/tenants/${slug}/companies/${companyId}`, { method: 'DELETE' }),

  checkCif: (cif: string) =>
    request<{ value: string; valid: boolean; foreign: boolean; reason: string | null }>('/api/admin/check-cif', {
      method: 'POST',
      body: JSON.stringify({ cif }),
    }),

  parameters: (slug: string) =>
    request<{
      definitions: { key: string; label: string; help: string; type: string; group: string; legacy?: string }[];
      values: Record<string, string>;
    }>(`/api/admin/tenants/${slug}/parameters`),

  saveParameters: (slug: string, values: Record<string, string>) =>
    request<{ values: Record<string, string>; health: TenantHealth }>(
      `/api/admin/tenants/${slug}/parameters`,
      { method: 'PUT', body: JSON.stringify({ values }) },
    ),

  roles: (slug: string) => request<{ roles: { id: string; name: string }[] }>(`/api/admin/tenants/${slug}/roles`),

  /** Prueba unas credenciales sin guardarlas. */
  probe: (
    slug: string,
    input: { companyDb: string; slUsername: string; slPassword: string; companyId?: string; cif?: string },
  ) =>
    request<{ ok: boolean; reached: boolean; ms: number; sapCode: number | string | null; message: string }>(
      `/api/admin/tenants/${slug}/probe`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  deleteTenant: (slug: string, confirm: string) =>
    request<{ slug: string; schema: string; fichajes: number; sociedades: number }>(
      `/api/admin/tenants/${slug}`,
      { method: 'DELETE', body: JSON.stringify({ confirm }) },
    ),

  testCompany: (slug: string, companyId: string) =>
    request<{
      ok: boolean; ms: number; message: string; cif?: string | null; sentCifHeader?: boolean;
      sapCode?: number | string | null; reached?: boolean;
    }>(
      `/api/admin/tenants/${slug}/companies/${companyId}/test`,
      { method: 'POST' },
    ),

  reload: (slug: string) =>
    request<{ ok: boolean; health: TenantHealth }>(`/api/admin/tenants/${slug}/reload`, { method: 'POST' }),
};

/** Envoltorio común de las acciones que se pueden encolar sin red. */
export function envelope() {
  return { clientEventId: crypto.randomUUID(), occurredAt: new Date().toISOString() };
}
